import * as React from 'react';
import { formFieldValidationHelper } from '../formField/FormField';
import { TextAreaWithSave } from '../inputs/TextAreaWithSave';
import { Flex, FlexItem } from './FlexHelpers';
import { EditableInputFadingButton, EditableInputWrapper } from './EditableText';
import { Icon } from '../icons/Icons';
import { InputMessage } from '../InputMessage';
import { colors } from '../colors/color';
import { textInputPadding } from '../inputs/TextInput';
import { useRef } from 'react';

type EditableTextAreaProps = {
    ariaLabel: string,
    value: string,
    placeholder: string,
    inErrorState?: boolean,
    infoMessage?: string,
    errorMessage?: string,
    disabled?: boolean,
    reserveMessageSpace?: boolean,
    validator?: (currentValue: string) => { valid: true; } | { valid: false, errorMessage: string; },
    onChange?: (value: string) => void,
    onCancel?: (cancelledValue: string, restoredValue: string) => void,
    onSave: (value: string, isValid: boolean) => void,
    fontSize?: 14 | 16,
    hideHoverEditButton?: boolean
};
export function EditableTextArea (props: EditableTextAreaProps): JSX.Element
{
    const [inEdit, setInEdit] = React.useState(false);
    const [currentValue, setCurrentValue] = React.useState(props.value);
    const inputRef = useRef(null);

    React.useEffect(() =>
    {
        // Sæt fokus på inputfeltet, når `inEdit` bliver `true`
        if (inEdit && inputRef.current)
        {
            inputRef.current.focus();
        }
    }, [inEdit]);

    React.useEffect(() =>
    {
        setCurrentValue(props.value);
    }, [props.value]);

    function handleChange (value: string)
    {
        setCurrentValue(value);
        if (props.onChange)
        {
            props.onChange(value);
        }
    }

    function handleCancel (cancelledValue: string, restoredValue: string)
    {
        setCurrentValue(restoredValue);
        if (props.onCancel)
        {
            props.onCancel(cancelledValue, restoredValue);
        }
        setInEdit(false);
    }

    function handleSave (newValue: string)
    {
        setCurrentValue(props.value);
        props.onSave(newValue, !inErrorState);
        setInEdit(false);
    }

    const { inErrorState, message } = formFieldValidationHelper(currentValue, props.infoMessage, props.errorMessage, props.validator);
    const { paddingVertical, paddingHorizontal } = textInputPadding(props.fontSize);
    const hideHoverButton = props.disabled===true || props.hideHoverEditButton === true;

    return (
        <FlexItem style={{flexGrow:1, width:'100%'}}>
            <EditableInputWrapper fontSize={props.fontSize || 16} paddingVertical={paddingVertical} paddingHorizontal={paddingHorizontal}>
                {inEdit &&
                    <>
                        <Flex flexDirection='row' alignItems="center">
                            <FlexItem flexGrow='1'>
                                <TextAreaWithSave
                                    innerRef={inputRef}
                                    ariaLabel={props.ariaLabel}
                                    autoFocus
                                    placeholder={props.placeholder}
                                    minRows={1}
                                    value={props.value}
                                    disabled={props.disabled}
                                    onSaveNoChange={() => setInEdit(false)}
                                    inErrorState={inErrorState}
                                    onChange={handleChange}
                                    onCancel={handleCancel}
                                    onBlur={() => setInEdit(false)}
                                    onSave={handleSave} />
                            </FlexItem>
                            <EditableInputFadingButton aria-label={`rediger ${props.ariaLabel}`}>
                                <Icon name='interface_actions_edit' />
                            </EditableInputFadingButton>
                        </Flex>
                    </>
                }
                {!inEdit &&
                    <Flex alignItems="center" justifyContent='stretch' onClick={() => !props.disabled && setInEdit(true)}>

                        <span
                            aria-label={props.ariaLabel}
                            style={{ 
                                whiteSpace: 'pre-wrap', 
                                flexGrow: '1', 
                                fontSize: '16px', 
                                color: inErrorState 
                                    ? colors.text.state.error
                                    : props.placeholder && props.value === "" 
                                        ? colors.text.inactive
                                        : undefined
                        }}>
                            {props.value || props.placeholder}
                        </span>

                        {!hideHoverButton &&
                            <EditableInputFadingButton aria-label={`rediger ${props.ariaLabel}`}>
                                <Icon name='interface_actions_edit' />
                            </EditableInputFadingButton>
                        }
                    </Flex>
                }

            </EditableInputWrapper>

            <div style={{ minHeight: props.reserveMessageSpace ? '30px' : undefined }}>
                {message !== undefined && message !== '' &&
                    <InputMessage inErrorState={inErrorState}>
                        {message}
                    </InputMessage>
                }
            </div>
        </FlexItem>
    );
}